import { useTranslation } from "react-i18next";

import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import {
  msUntilEnd,
  recentWinners,
  shortCountdownParts,
  URGENT_TIME_MS,
  winnersInLastHour,
} from "@/campaigns/modules/sprint-mission/types";
import {
  LatestWinsCard,
  type LatestWinRow,
} from "@/components/campaigns/latest-wins-card";

import { useSprintMissionPhase } from "./use-sprint-mission-phase";

/**
 * The "Latest wins" card of the Sprint Mission sidebar (#3958), as the Sprint-specific dressing
 * of the shared `LatestWinsCard`: the rows are the pool's recent-winners feed (masked names, the
 * reader's own row as "You"), and the pace line is Sprint policy — "10 winners in the last hour",
 * or the red "Ends in 38m" once the end is under an hour, nothing once the run has finished.
 * Nothing renders before the first win: the shared card owns that rule.
 */
export const SprintMissionLatestWinsCard = ({
  sprintMission,
  density,
  className,
}: {
  sprintMission: SprintMission;
  /** See `LatestWinsCard`. */
  density?: "default" | "dock";
  className?: string;
}) => {
  const { t } = useTranslation();
  const phase = useSprintMissionPhase(sprintMission);
  const finished = phase === "finished";

  const rows: LatestWinRow[] = recentWinners(sprintMission).map(
    (winner, index) => ({
      key: `${winner.slot ?? index}-${winner.won_at}`,
      // The generated schema types the hash loosely; anything but a string means "unknown", and
      // an unknown player is never the reader.
      player: typeof winner.player === "string" ? winner.player : null,
      name: winner.name,
      wonAt: winner.won_at,
    })
  );

  // "17d 4h" / "4h 32m" / "38m" through the dictionary, so the unit letters translate.
  const shortCountdown = (ms: number): string => {
    const parts = shortCountdownParts(ms);
    switch (parts.unit) {
      case "days":
        return t("sprintMission.countdownDays", "{{days}}d {{hours}}h", parts);
      case "hours":
        return t(
          "sprintMission.countdownHours",
          "{{hours}}h {{minutes}}m",
          parts
        );
      default:
        return t("sprintMission.countdownMinutes", "{{minutes}}m", parts);
    }
  };

  const renderPace = (now: number) => {
    if (finished) return null;
    const msLeft = msUntilEnd(sprintMission, now);
    const closingSoon =
      msLeft !== null && msLeft > 0 && msLeft <= URGENT_TIME_MS;
    if (closingSoon) {
      return (
        <p className="flex items-center gap-2 font-heading text-[12px] leading-4 font-semibold text-urgent lg:text-[13px]">
          <span aria-hidden className="size-1.5 rounded-full bg-urgent" />
          {t("sprintMission.endsIn", "Ends in {{time}}", {
            time: shortCountdown(msLeft),
          })}
        </p>
      );
    }
    const pace = winnersInLastHour(sprintMission, now);
    if (pace.count === 0) return null;
    return (
      <p className="flex items-center gap-2 font-heading text-[12px] leading-4 font-semibold text-foreground lg:text-[13px]">
        <span aria-hidden className="size-1.5 rounded-full bg-active" />
        {pace.plus
          ? t(
              "sprintMission.winnersLastHourPlus",
              "{{count}}+ winners in the last hour",
              { count: pace.count }
            )
          : t(
              "sprintMission.winnersLastHour",
              "{{count}} winners in the last hour",
              { count: pace.count }
            )}
      </p>
    );
  };

  return (
    <LatestWinsCard
      testId="sprint-mission-latest-wins"
      title={t("sprintMission.latestWins", "Latest wins")}
      rows={rows}
      renderPace={renderPace}
      finished={finished}
      density={density}
      className={className}
    />
  );
};
